<template>
  <div>
    <div class="breadcrumbs mb-2">
      <v-breadcrumbs divider=">" :items="breadcrumbsList">
        <template v-slot:item="{ item }">
          <v-breadcrumbs-item
            :to="{ name: item.path }"
            :disabled="item.disabled"
            exact
          >
            {{ item.text }}
          </v-breadcrumbs-item>
        </template>
      </v-breadcrumbs>
    </div>
    <PageTitle pageHeading="Groups" :rightsideButton="true">
      <v-btn dark class="font-weight-bold px-2" @click="toggleNewEditGroup()">
        <v-icon> mdi-plus </v-icon>
        <span class="pl-2 pr-3"> New Group </span>
      </v-btn>
    </PageTitle>

    <v-data-table
      :headers="headers"
      :items="groupList"
      :items-per-page="10"
      :options.sync="pagination"
      :server-items-length="totalItems"
      :footer-props="{
        'items-per-page-options': [10, 25, 50, 75, 100],
      }"
      class="elevation-1 mt-8"
      :loading="loading"
    >
      <template v-slot:[`item.name`]="{ item }">
        {{ item.name | upperCaseFirst }}
      </template>
      <template v-slot:[`item.description`]="{ item }">
        <v-tooltip left max-width="400">
          <template v-slot:activator="{ on }">
            <span
              class="body-1 word-break text-ellipsis-twolines cursor-pointer"
              v-on="on"
            >
              {{ item.description | upperCaseFirst }}
            </span>
          </template>
          <span class="word-break">{{
            item.description | upperCaseFirst
          }}</span>
        </v-tooltip>
      </template>
      <template v-slot:[`item.service_hour_name`]="{ item }">
        <span v-if="item.service_hour_name">{{
          item.service_hour_name | upperCaseFirst
        }}</span>
        <span v-else class="text-center"> -- </span>
      </template>
      <template v-slot:[`item.action`]="{ item }">
        <span @click.stop class="body-1">
          <v-menu left bottom offset-y>
            <template v-slot:activator="{ on }">
              <v-btn v-on="on" icon>
                <v-icon class="font-weight-bold">mdi-dots-vertical</v-icon>
              </v-btn>
            </template>

            <v-list dense class="pa-0">
              <v-list-item-group>
                <v-list-item id="edit-type" link @click="showGroup(item)">
                  <v-list-item-icon class="mr-1">
                    <v-icon size="20"> mdi-pencil </v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="body-1 font-weight-regular"
                      >Edit</v-list-item-title
                    >
                  </v-list-item-content>
                </v-list-item>
                <v-list-item
                  id="delete-type"
                  link
                  @click="toggleDeleteDialog(item)"
                >
                  <v-list-item-icon class="mr-1">
                    <v-icon size="20">delete</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="body-1 font-weight-regular"
                      >Delete</v-list-item-title
                    >
                  </v-list-item-content>
                </v-list-item>
              </v-list-item-group>
            </v-list>
          </v-menu>
        </span>
      </template>
    </v-data-table>

    <v-navigation-drawer
      v-model="groupAddEditDrawer"
      class="px-6 pt-6 overflow-y-hidden pr-2 groups"
      id="add-edit-user-drawer"
      width="550px"
      fixed
      right
      temporary
    >
      <div class="mb-5 fixed-drawer pr-8 pl-2">
        <v-row no-gutters>
          <v-col align="left">
            <h2
              class="
                secondary--font
                font-weight-medium-custom
                float-left
                text-capitalize
              "
            >
              {{ action }} Group
            </h2>
          </v-col>
          <v-col align="right">
            <v-btn
              icon
              @click.stop="groupAddEditDrawer = false"
              class="float-right"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-col>
          <v-col cols="12 py-2 pt-5">
            <span v-if="action == 'new'">
              Provide the following details to create a new group.
            </span>
            <span v-else> Edit Group with below details. </span>
          </v-col>
        </v-row>
        <v-card class="mt-5" elevation="0">
          <v-form class="mt-5">
            <v-card-text class="px-0">
              <v-text-field
                class="pb-2 required"
                label="Group Name"
                outlined
                v-model="groupName"
                @input="$v.groupName.$touch()"
                @blur="$v.groupName.$touch()"
                :error-messages="groupNameError"
              ></v-text-field>

              <v-textarea
                name="description"
                outlined
                label="Description"
                auto-grow
                required
                v-model="groupDescription"
              ></v-textarea>

              <v-autocomplete
                v-model="selectedAgent"
                :items="agentList"
                item-text="name"
                item-value="id"
                label="Agent"
                class="customize-select"
                outlined
                deletable-chips
                chips
                multiple
                required
              ></v-autocomplete>

              <v-autocomplete
                v-model="selectedHours"
                :items="serviceHoursList"
                item-text="name"
                item-value="id"
                label="Service hours"
                class="customize-select"
                outlined
                clearable
                :no-filter="true"
                :search-input.sync="searchKeyword"
              ></v-autocomplete>
            </v-card-text>
          </v-form>
        </v-card>
      </div>
      <template v-slot:append>
        <div class="pa-2">
          <v-card-actions class="px-0 d-flex align-end">
            <v-btn
              class="font-weight-bold mb-4"
              outlined
              @click.stop="groupAddEditDrawer = false"
              >Cancel</v-btn
            >
            <v-btn
              :dark="groupName ? true : false"
              :disabled="!groupName ? true : false"
              class="font-weight-bold mb-4"
              @click="createNewGroup()"
              >Save</v-btn
            >
          </v-card-actions>
        </div>
      </template>
    </v-navigation-drawer>

    <v-dialog v-model="deleteDialog" max-width="500">
      <v-card class="pa-2 py-3">
        <v-card-title class="text-h6 font-weight-medium-custom">
          <span class="secondary--font"> Delete Group </span>
        </v-card-title>
        <v-card-text class="body-2">
          Are you sure you want to delete the group ({{
            selectedData.name | upperCaseFirst
          }})
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false" class="font-weight-bold">
            Cancel
          </v-btn>
          <v-btn dark @click="confirmDelete()" class="font-weight-bold">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import PageTitle from "@/components/shared/PageTitle";
import { validationMixin } from "vuelidate";
import { required } from "vuelidate/lib/validators";
import { groupTypeMixin } from "@/mixins/grouptype";
import { $servicehours } from "@/services/Index";
import { $group } from "@/services/Index";

export default {
  name: "agent-group",
  mixins: [validationMixin, groupTypeMixin],
  validations: {
    groupName: { required },
  },
  data: () => {
    return {
      groupAddEditDrawer: false,
      groupName: null,
      groupDescription: null,
      deleteDialog: false,
      selectedData: [],
      breadcrumbsList: [
        {
          text: "Settings",
          disabled: false,
          path: "settings",
        },
        {
          text: "Groups",
          disabled: true,
          path: "",
        },
      ],
      headers: [
        {
          text: "Name",
          align: "start",
          value: "name",
          width: "20%",
        },
        { text: "Description", value: "description", width: "30%" },
        { text: " Service hours", value: "service_hour_name", width: "20%" },
        { text: "No. Agents", value: "agents" },
        {
          text: "Actions",
          value: "action",
          align: "right",
          sortable: false,
        },
      ],
      groupList: [],
      totalItems: 0,
      pagination: {},
      loading: false,
      action: "new",
      selectedAgent: null,
      agentList: [
        { name: "agent1", id: 1 },
        { name: "agent2", id: 2 },
        { name: "agent3", id: 3 },
        { name: "agent4", id: 4 },
      ],
      serviceHoursList: [],
      selectedHours: null,
      page: 1,
      searchKeyword: null,
    };
  },

  components: {
    PageTitle,
  },

  computed: {
    groupNameError() {
      const errors = [];
      if (!this.$v.groupName.$dirty) return errors;
      !this.$v.groupName.required && errors.push("Group name is required");
      return errors;
    },
  },

  watch: {
    pagination: {
      handler(newVal, oldVal) {
        /** prevent this check to invoked function on page load */
        if (oldVal.itemsPerPage) {
          this.getGroupList();
        }
      },
      deep: true,
    },

    searchKeyword(val) {
      if (val && val.length >= 3) {
        let searchVal = val;
        this.getServiceHours(searchVal);
      }
    },

    /*** temporary code for global search */
    $route: {
      handler: function (to, from) {
        this.globalSearchKeyword = "";
        let name = to.query.name;
        if (name) {
          this.globalSearchKeyword = name;
          this.getGroupList();
        } else {
          this.getGroupList();
        }
      },
      deep: true,
      immediate: true,
    },
  },

  mounted() {
    this.getGroupList();
    this.getServiceHours();
  },
  methods: {
    toggleDeleteDialog(data) {
      this.deleteDialog = true;
      this.selectedData = data;
    },

    /** onclick of new group show drawer */
    toggleNewEditGroup() {
      this.groupAddEditDrawer = true;
      this.action = "new";
      this.resetForm();
    },

    /** Reseting blank values for create group form */
    resetForm() {
      this.$v.$reset();
      this.groupName = null;
      this.selectedAgent = null;
      this.groupDescription = null;
      this.selectedHours = null;
    },

    /** get Group list data from API's */
    async getGroupList() {
      this.loading = true;
      const { page, itemsPerPage, sortBy, sortDesc } = this.pagination;
      const data = {
        page,
        per_page: itemsPerPage,
        q: {
          name_cont: this.globalSearchKeyword,
          description_cont: this.globalSearchKeyword,
          id_in: this.globalSearchKeyword,
          m: "or",
        },
      };
      // If sorting ..
      if (sortBy && sortBy.length) {
        const dir = sortDesc.length
          ? sortDesc[0] === true
            ? "desc"
            : "asc"
          : "";
        data.q.s = `${sortBy[0]} ${dir}`;
      }
      try {
        const response = await $group.fetchList(data, {});
        if (response.data) {
          this.totalItems = response.data.pagination.total_count;
          this.groupList = response.data.groups;
          this.loading = false;
        }
      } catch (error) {
        this.loading = false;
      }
    },

    /** creating new group functionality */
    async createNewGroup() {
      if (this.action == "new") {
        this.$v.$touch();
        let payload = {
          group: {
            name: this.groupName.toLowerCase(),
            description: this.groupDescription,
            agent_ids: this.selectedAgent,
            service_hour_id: this.selectedHours,
          },
        };

        if (!this.$v.$invalid) {
          this.loading = true;
          try {
            const response = await $group.create(payload, {});
            if (response.data) {
              this.$store.dispatch("ui/snackbar", [
                {
                  title: "New Group",
                  message: `A new group has been created`,
                  type: "success",
                },
              ]);
              this.getGroupList();
              this.groupAddEditDrawer = false;
              this.loading = false;
            }
          } catch (error) {
            this.loading = false;
            if (error.response !== undefined) {
              this.$store.dispatch("ui/snackbar", [
                {
                  title: "Error",
                  message: this.$errors(error.response.data).msg({
                    capitalize: false,
                    prepend: `${this.$errors(
                      error.response.data
                    ).subjectText()} `,
                  }),
                  type: "error",
                },
              ]);
            }
          }
        }
      } else {
        this.updateGroup();
      }
    },

    /* eslint-disable */
    /** onclick of edit showing selected row group data */
    async showGroup(data) {
      this.groupAddEditDrawer = true;
      this.action = "edit";
      try {
        const response = await $group.showGroup(data, {});
        if (response.data) {
          this.selectedData = response.data;
          this.groupName = this.selectedData.name;
          this.groupDescription = this.selectedData.description;
          this.selectedAgent = this.selectedData.agent_ids;
          this.selectedHours = this.selectedData.service_hour_id;
          this.groupAddEditDrawer = true;
          this.action = "edit";
        }
      } catch (error) {
        if (error.response !== undefined) {
          this.$store.dispatch("ui/snackbar", [
            {
              title: "Error",
              message: this.$errors(error.response.data).msg({
                capitalize: false,
                prepend: `${this.$errors(error.response.data).subjectText()} `,
              }),
              type: "error",
            },
          ]);
        }
      }
    },
    /* eslint-disable */
    /** updateGroup group API request. */
    async updateGroup() {
      let payload = {
        group: {
          name: this.groupName,
          description: this.groupDescription,
          agent_ids: this.selectedAgent,
          service_hour_id: this.selectedHours,
        },
      };
      try {
        const response = await $group.update(this.selectedData.id, payload, {});
        if (response.data) {
          this.groupAddEditDrawer = false;
          this.getGroupList();
          this.$store.dispatch("ui/snackbar", [
            {
              title: "",
              message: `Group updated`,
              type: "success",
            },
          ]);
        }
      } catch (error) {
        if (error.response !== undefined) {
          this.$store.dispatch("ui/snackbar", [
            {
              title: "Error",
              message: this.$errors(error.response.data).msg({
                capitalize: false,
                prepend: `${this.$errors(error.response.data).subjectText()} `,
              }),
              type: "error",
            },
          ]);
        }
      }
    },

    /** deleting selected group API request */
    async confirmDelete() {
      let payload = {
        id: this.selectedData.id,
      };
      try {
        const response = await $group.delete(payload, {});
        if (response.data) {
          this.deleteDialog = false;
          this.getGroupList();
          this.checkFiltersInSession(payload.id);
          this.$store.dispatch("ui/snackbar", [
            {
              title: "Delete Group",
              message: `Selected group has been deleted.`,
              type: "success",
            },
          ]);
        }
      } catch (error) {
        this.deleteDialog = false;
      }
    },

    /* eslint-disable */
    /* getting list of service to link group with service hours */
    async getServiceHours(searchVal = "") {
      const data = {
        page: this.page,
        per_page: 10,
        q: {
          name_cont: searchVal,
          id_in: searchVal,
          m: "or",
        },
      };
      try {
        const response = await $servicehours.fetchList(data, {});
        if (response.data) {
          this.serviceHoursList = response.data.service_hours;
        }
      } catch (error) {}
    },
  },
};
</script>

<style lang="scss"></style>
